(() => {
  const CART_SELECTION_KEY = 'furavoraCartSelectionV1';
  const CART_SAVED_KEY = 'furavoraSavedCartItemsV1';
  const preferredScrollBehavior = () => document.documentElement.dataset.fvMotion === 'reduce' ? 'auto' : 'smooth';

  const syncBodyLock = () => {
    const overlayOpen = document.querySelector('[data-fv2-menu][open]');
    document.body.classList.toggle('fv2-lock', Boolean(overlayOpen));
  };

  const closeDetails = (details) => {
    if (details) details.removeAttribute('open');
    syncBodyLock();
  };

  const safeRead = (storage, key, fallback = {}) => {
    try {
      return JSON.parse(storage.getItem(key)) || fallback;
    } catch (_error) {
      return fallback;
    }
  };

  const safeWrite = (storage, key, value) => {
    try {
      storage.setItem(key, JSON.stringify(value));
      return true;
    } catch (_error) {
      // Storage may be disabled. Cart selection still works for the current page.
      return false;
    }
  };

  const refreshCartCount = async () => {
    if (!window.routes?.cart_url) return;
    const response = await fetch(`${window.routes.cart_url}.js`, { credentials: 'same-origin' });
    if (!response.ok) return;
    const cart = await response.json();
    document.querySelectorAll('.fv2-cart b').forEach((count) => { count.textContent = cart.item_count; });
    document.querySelectorAll('.fv2-cart').forEach((link) => {
      link.setAttribute('aria-label', `Cart with ${cart.item_count} items`);
    });
  };

  const initControls = () => {
    document.querySelectorAll('[data-fv2-menu]').forEach((menu) => {
      if (menu.dataset.ready) return;
      menu.dataset.ready = 'true';
      const trigger = menu.querySelector('summary');
      const drawer = menu.querySelector('.fv2-menu__drawer');
      menu.addEventListener('toggle', () => {
        syncBodyLock();
        if (!menu.open) return;
        document.querySelectorAll('[data-fv2-search][open]').forEach(closeDetails);
        window.requestAnimationFrame(() => menu.querySelector('.fv2-menu__close')?.focus({ preventScroll: true }));
      });
      menu.querySelectorAll('[data-fv2-menu-close]').forEach((element) => {
        element.addEventListener('click', () => {
          closeDetails(menu);
          trigger?.focus({ preventScroll: true });
        });
      });
      menu.querySelectorAll('a').forEach((link) => link.addEventListener('click', () => closeDetails(menu)));
      menu.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          closeDetails(menu);
          trigger?.focus({ preventScroll: true });
          return;
        }
        if (event.key !== 'Tab' || !menu.open || !drawer) return;
        const focusable = [...drawer.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')]
          .filter((element) => !element.hidden && element.getAttribute('aria-hidden') !== 'true');
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      });
    });

    document.querySelectorAll('[data-fv2-search]').forEach((search) => {
      if (search.dataset.ready) return;
      search.dataset.ready = 'true';
      const trigger = search.querySelector('summary');
      search.addEventListener('toggle', () => {
        syncBodyLock();
        if (search.open) {
          document.querySelectorAll('[data-fv2-menu][open]').forEach(closeDetails);
          window.setTimeout(() => search.querySelector('input[type="search"]')?.focus(), 80);
        }
      });
      search.querySelector('[data-fv2-search-close]')?.addEventListener('click', () => {
        closeDetails(search);
        trigger?.focus({ preventScroll: true });
      });
    });
  };

  const defaultCarrierToSmall = () => {
    if (!location.pathname.includes('/products/shoulder-and-crossbody-pet-dog-backpack-small-dog-leash-handbag')) return;
    if (new URLSearchParams(location.search).has('variant')) return;
    const small = [...document.querySelectorAll('variant-selects input[type="radio"], variant-radios input[type="radio"]')]
      .find((input) => input.value.trim().toUpperCase() === 'S');
    if (small && !small.checked) small.click();
  };

  const initHomeCategoryFilters = () => {
    const trending = document.querySelector('#furavora-trending');
    if (!trending || trending.dataset.fvCategoryReady) return;
    const items = [...trending.querySelectorAll('[data-fv-product-categories]')];
    const heading = trending.querySelector('[data-fv-category-heading]');
    const reset = trending.querySelector('[data-fv-category-reset]');
    const circles = [...document.querySelectorAll('[data-fv-category]')];
    if (!items.length || !circles.length) return;
    trending.dataset.fvCategoryReady = 'true';

    const showCategory = (category, label, shouldMoveFocus = true) => {
      let shown = 0;
      items.forEach((item) => {
        const matches = item.dataset.fvProductCategories.split(/\s+/).includes(category);
        item.hidden = !matches;
        if (matches) shown += 1;
      });
      if (!shown) return false;
      circles.forEach((circle) => {
        const active = circle.dataset.fvCategory === category;
        circle.classList.toggle('is-active', active);
        circle.setAttribute('aria-pressed', String(active));
      });
      if (heading) heading.textContent = label || category.replace(/-/g, ' ').toUpperCase();
      if (reset) reset.hidden = false;
      const behavior = preferredScrollBehavior();
      trending.querySelector('.fv4-trending__track')?.scrollTo({ left: 0, behavior });
      trending.scrollIntoView({ behavior, block: 'start' });
      if (shouldMoveFocus) window.setTimeout(() => heading?.focus({ preventScroll: true }), behavior === 'auto' ? 0 : 450);
      return true;
    };

    const showAll = (shouldScroll = true) => {
      items.forEach((item) => { item.hidden = false; });
      circles.forEach((circle) => {
        circle.classList.remove('is-active');
        circle.setAttribute('aria-pressed', 'false');
      });
      if (heading) heading.textContent = heading.dataset.defaultHeading;
      if (reset) reset.hidden = true;
      const behavior = preferredScrollBehavior();
      trending.querySelector('.fv4-trending__track')?.scrollTo({ left: 0, behavior });
      if (shouldScroll) trending.scrollIntoView({ behavior, block: 'start' });
    };

    circles.forEach((circle) => {
      circle.setAttribute('aria-pressed', 'false');
      circle.addEventListener('click', (event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        showCategory(circle.dataset.fvCategory, circle.querySelector('strong')?.textContent.trim());
      });
      circle.addEventListener('keydown', (event) => {
        if (event.key !== ' ') return;
        event.preventDefault();
        showCategory(circle.dataset.fvCategory, circle.querySelector('strong')?.textContent.trim());
      });
    });

    if (heading && !heading.hasAttribute('tabindex')) heading.tabIndex = -1;
    reset?.addEventListener('click', () => showAll(false));
    document.querySelectorAll('[data-fv-global-category]').forEach((link) => {
      link.addEventListener('click', (event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        const category = link.dataset.fvGlobalCategory || 'all';
        if (category === 'all') showAll(true);
        else showCategory(category, category.replace(/-/g, ' ').toUpperCase(), false);
      });
    });
  };

  const initCatalog = () => {
    document.querySelectorAll('[data-fv-catalog]').forEach((catalog) => {
      if (catalog.dataset.ready) return;
      catalog.dataset.ready = 'true';
      const cards = [...catalog.querySelectorAll('[data-fv-catalog-card]')];
      const categories = [...catalog.querySelectorAll('[data-fv-catalog-category]')];
      const heading = catalog.querySelector('[data-fv-catalog-heading]');
      const count = catalog.querySelector('[data-fv-catalog-count]');
      const empty = catalog.querySelector('[data-fv-catalog-empty]');
      const input = catalog.querySelector('[data-fv-catalog-search]');
      const form = catalog.querySelector('[data-fv-catalog-search-form]');
      const labels = {
        all: 'ALL PRODUCTS',
        'play-interactive': 'PLAY & INTERACTIVE',
        'care-home': 'CARE & HOME',
        'walk-travel': 'WALK & TRAVEL',
        'cozy-style': 'COZY & STYLE',
      };
      const metadata = {
        all: {
          title: 'Shop All Pet Essentials | Furavora',
          description: 'Shop Furavora pet essentials for play, grooming, hydration, comfort, walks, travel, and everyday life.',
        },
        'play-interactive': {
          title: 'Play & Interactive Pet Toys | Furavora',
          description: 'Shop engaging Furavora toys that encourage active, curious play for dogs and cats.',
        },
        'care-home': {
          title: 'Pet Care & Home Essentials | Furavora',
          description: 'Shop Furavora grooming, cleanup, hydration, and everyday home-care essentials for dogs and cats.',
        },
        'walk-travel': {
          title: 'Pet Walk & Travel Essentials | Furavora',
          description: 'Shop Furavora harnesses, carriers, and practical gear for comfortable walks and travel together.',
        },
        'cozy-style': {
          title: 'Cozy Pet Style & Comfort | Furavora',
          description: 'Shop comfortable Furavora outfits, costumes, beds, and cozy essentials for dogs and cats.',
        },
      };

      const syncCatalogMetadata = (category, query) => {
        const cleanQuery = query.trim();
        const selected = metadata[category] || metadata.all;
        if (cleanQuery) document.title = `Search “${cleanQuery}” | Furavora`;
        else document.title = selected.title;

        const description = document.querySelector('meta[name="description"]');
        if (description && !cleanQuery) description.setAttribute('content', selected.description);
      };

      const filterCatalog = (category = 'all', query = '', updateUrl = true) => {
        const cleanQuery = query.trim().toLowerCase();
        let visible = 0;
        cards.forEach((card) => {
          const cardCategories = card.dataset.fvProductCategories.split(/\s+/);
          const categoryMatch = category === 'all' || cardCategories.includes(category);
          const searchMatch = !cleanQuery || card.dataset.fvSearchText.includes(cleanQuery);
          card.hidden = !(categoryMatch && searchMatch);
          if (!card.hidden) visible += 1;
        });
        categories.forEach((link) => {
          const active = link.dataset.fvCatalogCategory === category && !cleanQuery;
          link.classList.toggle('is-active', active);
          if (active) link.setAttribute('aria-current', 'page');
          else link.removeAttribute('aria-current');
        });
        if (heading) heading.textContent = cleanQuery ? `RESULTS FOR “${query.trim().toUpperCase()}”` : (labels[category] || labels.all);
        if (count) count.textContent = `${visible} ${visible === 1 ? 'PRODUCT' : 'PRODUCTS'}`;
        if (empty) empty.hidden = visible !== 0;
        syncCatalogMetadata(category, query);
        if (updateUrl) {
          let url;
          if (cleanQuery) {
            url = new URL(location.href);
            url.search = '';
            url.searchParams.set('q', query.trim());
          } else {
            const categoryLink = categories.find((link) => link.dataset.fvCatalogCategory === category);
            url = new URL(categoryLink?.href || categories[0]?.href || location.href, location.origin);
          }
          history.pushState({ furavoraCategory: category, furavoraQuery: query.trim() }, '', `${url.pathname}${url.search}`);
        }
      };

      categories.forEach((link) => {
        link.addEventListener('click', (event) => {
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          event.preventDefault();
          if (input) input.value = '';
          filterCatalog(link.dataset.fvCatalogCategory, '', true);
        });
      });
      document.querySelectorAll('[data-fv-global-category]').forEach((link) => {
        link.addEventListener('click', (event) => {
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          event.preventDefault();
          if (input) input.value = '';
          filterCatalog(link.dataset.fvGlobalCategory || 'all', '', true);
          catalog.scrollIntoView({ behavior: preferredScrollBehavior(), block: 'start' });
        });
      });

      form?.addEventListener('submit', (event) => {
        event.preventDefault();
        filterCatalog('all', input?.value || '', true);
      });
      input?.addEventListener('search', () => filterCatalog('all', input.value, true));
      catalog.querySelector('[data-fv-catalog-clear]')?.addEventListener('click', () => {
        if (input) input.value = '';
        filterCatalog('all', '', true);
      });

      const params = new URLSearchParams(location.search);
      const urlCategory = params.get('category');
      const urlQuery = params.get('q') || '';
      const initialQuery = !urlCategory ? (urlQuery || input?.value || catalog.dataset.initialQuery || '') : '';
      const initialCategory = labels[urlCategory]
        ? urlCategory
        : (initialQuery ? 'all' : catalog.dataset.initialCategory);
      filterCatalog(initialCategory || 'all', initialQuery, false);

      window.addEventListener('popstate', (event) => {
        const current = new URLSearchParams(location.search);
        const popCategory = current.get('category');
        const popQuery = current.get('q') || '';
        if (input) input.value = popQuery;
        filterCatalog(labels[popCategory] ? popCategory : 'all', popQuery, false);
      });
    });
  };

  const moneyWithDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
    if (number == null || Number.isNaN(Number(number))) return '0';
    const value = (Number(number) / 100).toFixed(precision);
    const [whole, fraction] = value.split('.');
    const withThousands = whole.replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
    return fraction ? `${withThousands}${decimal}${fraction}` : withThousands;
  };

  const formatCartMoney = (cents, format) => {
    const moneyFormat = format || '${{amount}}';
    const match = moneyFormat.match(/\{\{\s*(\w+)\s*\}\}/);
    const placeholder = match?.[1] || 'amount';
    let value;
    switch (placeholder) {
      case 'amount_no_decimals':
        value = moneyWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = moneyWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = moneyWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_space_separator':
        value = moneyWithDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = moneyWithDelimiters(cents, 0, ' ', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = moneyWithDelimiters(cents, 2, "'", '.');
        break;
      default:
        value = moneyWithDelimiters(cents, 2);
    }
    return moneyFormat.replace(/\{\{\s*\w+\s*\}\}/, value);
  };

  const currentSelection = () => {
    const selected = new Map();
    document.querySelectorAll('[data-fv-cart-select]').forEach((input) => {
      selected.set(input.dataset.lineKey, input.checked);
    });
    return selected;
  };

  const syncCartTotals = (selection = currentSelection()) => {
    const uniqueLines = new Map();
    document.querySelectorAll('[data-fv-cart-line]').forEach((line) => {
      const key = line.dataset.fvLineKey;
      if (!key || uniqueLines.has(key)) return;
      uniqueLines.set(key, {
        promotional: Number(line.dataset.fvLineTotalCents || 0),
        regular: Number(line.dataset.fvLineRegularTotalCents || line.dataset.fvLineTotalCents || 0),
      });
    });

    let selectedTotal = 0;
    let selectedRegularTotal = 0;
    let fullTotal = 0;
    uniqueLines.forEach((lineTotals, key) => {
      fullTotal += lineTotals.promotional;
      if (selection.get(key) !== false) {
        selectedTotal += lineTotals.promotional;
        selectedRegularTotal += lineTotals.regular;
      }
    });
    const selectedCount = [...selection.values()].filter(Boolean).length;
    const totalCount = selection.size;

    document.querySelectorAll('[data-fv-cart-totals]').forEach((totals) => {
      const format = totals.dataset.moneyWithCurrencyFormat || totals.dataset.moneyFormat || '${{amount}}';
      const fallbackFull = Number(totals.dataset.fvFullTotalCents || fullTotal || 0);
      const selectedValue = totals.querySelector('[data-fv-selected-total-value]');
      const fullValue = totals.querySelector('[data-fv-full-total-value]');
      const fullLine = totals.querySelector('[data-fv-full-total-line]');
      const note = totals.querySelector('[data-fv-selected-total-note]');
      if (selectedValue) {
        selectedValue.textContent = formatCartMoney(selectedTotal, format);
        selectedValue.classList.remove('fv-cart-total-pop');
        requestAnimationFrame(() => selectedValue.classList.add('fv-cart-total-pop'));
      }
      if (fullValue) fullValue.textContent = formatCartMoney(fallbackFull, format);
      if (fullLine) fullLine.hidden = selectedCount === totalCount;
      if (note) {
        note.textContent = selectedCount === totalCount
          ? 'All items are selected.'
          : `${selectedCount} of ${totalCount} ${totalCount === 1 ? 'item is' : 'items are'} selected for checkout.`;
      }
    });

    document.querySelectorAll('[data-fv-cart-promo-summary]').forEach((summary) => {
      const format = document.querySelector('[data-fv-cart-totals]')?.dataset.moneyFormat || '${{amount}}';
      const percent = summary.dataset.fvPromoPercent || '';
      const savings = Math.max(0, selectedRegularTotal - selectedTotal);
      const copy = summary.querySelector('p');
      if (copy) copy.textContent = savings > 0
        ? `${percent}% automatic offer saves ${formatCartMoney(savings, format)} on the selected items. Final discount is confirmed in Shopify checkout.`
        : 'The final automatic discount is confirmed in Shopify checkout.';
    });

    document.querySelectorAll('[data-fv-shipping-progress]').forEach((progress) => {
      const threshold = Number(progress.dataset.thresholdCents || 10000);
      const format = progress.dataset.moneyFormat || '${{amount}}';
      const remaining = Math.max(0, threshold - selectedTotal);
      const percent = threshold > 0 ? Math.min(100, Math.max(0, (selectedTotal / threshold) * 100)) : 100;
      const copy = progress.querySelector('[data-fv-shipping-progress-copy]');
      const bar = progress.querySelector('[data-fv-shipping-progress-bar]');
      const track = progress.querySelector('[role="progressbar"]');
      if (copy) copy.textContent = remaining === 0
        ? "YOU'VE UNLOCKED FREE WORLDWIDE SHIPPING!"
        : `ADD ${formatCartMoney(remaining, format)} MORE FOR FREE WORLDWIDE SHIPPING`;
      if (bar) bar.style.width = `${percent}%`;
      if (track) track.setAttribute('aria-valuenow', String(Math.round(percent)));
      progress.classList.toggle('is-unlocked', remaining === 0);
    });
  };

  const syncCartSelection = () => {
    const inputs = [...document.querySelectorAll('[data-fv-cart-select]')];
    if (!inputs.length) return;
    const stored = safeRead(sessionStorage, CART_SELECTION_KEY, {});
    inputs.forEach((input) => {
      if (!input.dataset.ready) {
        input.dataset.ready = 'true';
        if (Object.prototype.hasOwnProperty.call(stored, input.dataset.lineKey)) input.checked = Boolean(stored[input.dataset.lineKey]);
      }
      const row = input.closest('.cart-item');
      row?.classList.toggle('fv-cart-line--excluded', !input.checked);
      const state = row?.querySelector('[data-fv-cart-item-state]');
      if (state) state.textContent = input.checked ? 'Ready for checkout' : 'Saved in cart — not checking out';
    });

    const selection = currentSelection();
    const selectedCount = [...selection.values()].filter(Boolean).length;
    const totalCount = selection.size;
    document.querySelectorAll('[data-fv-cart-select-all]').forEach((control) => {
      control.checked = selectedCount === totalCount && totalCount > 0;
      control.indeterminate = selectedCount > 0 && selectedCount < totalCount;
    });
    document.querySelectorAll('[data-fv-cart-selection-summary]').forEach((summary) => {
      summary.textContent = summary.closest('.fv-cart-select-bar--drawer') ? 'Selected items' : 'Choose the items you want to check out.';
    });
    document.querySelectorAll('[data-fv-checkout-selected]').forEach((button) => {
      button.disabled = selectedCount === 0;
      button.setAttribute('aria-disabled', String(selectedCount === 0));
      if (!button.classList.contains('is-loading')) {
        button.textContent = selectedCount > 0
          ? 'CHECK OUT →'
          : 'SELECT AN ITEM';
      }
    });
    document.documentElement.classList.toggle('fv-cart-has-unselected', selectedCount < totalCount);
    safeWrite(sessionStorage, CART_SELECTION_KEY, Object.fromEntries(selection));
    syncCartTotals(selection);
  };

  const syncSavedCartNotice = () => {
    const saved = safeRead(localStorage, CART_SAVED_KEY, { items: [] });
    const itemCount = Array.isArray(saved.items) ? saved.items.reduce((sum, item) => sum + Number(item.quantity || 0), 0) : 0;
    document.querySelectorAll('[data-fv-saved-cart]').forEach((notice) => {
      notice.hidden = itemCount === 0;
      const copy = notice.querySelector('[data-fv-saved-cart-copy]');
      if (copy) copy.textContent = `${itemCount} ${itemCount === 1 ? 'item was' : 'items were'} kept aside from the last checkout.`;
    });
  };

  const checkoutSelected = async (button) => {
    const selection = currentSelection();
    const selectedKeys = new Set([...selection].filter(([, isSelected]) => isSelected).map(([key]) => key));
    const statusElements = document.querySelectorAll('[data-fv-cart-selection-status]');
    const setStatus = (message) => statusElements.forEach((status) => { status.textContent = message; });
    if (!selectedKeys.size) {
      setStatus('Select at least one item to continue.');
      return;
    }

    const originalLabels = new Map();
    document.querySelectorAll('[data-fv-checkout-selected]').forEach((checkoutButton) => {
      originalLabels.set(checkoutButton, checkoutButton.textContent);
      checkoutButton.disabled = true;
      checkoutButton.classList.add('is-loading');
      checkoutButton.textContent = 'PREPARING CHECKOUT…';
    });
    setStatus('Preparing only the items you selected. Unchecked items will return when you come back to your cart.');

    try {
      const cartResponse = await fetch(`${window.routes.cart_url}.js`, { credentials: 'same-origin' });
      if (!cartResponse.ok) throw new Error('Could not read your cart.');
      const cart = await cartResponse.json();
      const unselected = cart.items.filter((item) => !selectedKeys.has(item.key));
      if (unselected.length) {
        const savedItems = unselected.map((item) => ({
          id: item.variant_id,
          quantity: item.quantity,
          properties: item.properties || {},
          selling_plan: item.selling_plan_allocation?.selling_plan?.id || undefined,
        }));
        if (!safeWrite(localStorage, CART_SAVED_KEY, { items: savedItems, savedAt: Date.now() })) {
          throw new Error('Browser storage is unavailable. Select all items before checkout.');
        }
        const updates = Object.fromEntries(unselected.map((item) => [item.key, 0]));
        const updateResponse = await fetch(window.routes.cart_update_url, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates }),
        });
        if (!updateResponse.ok) {
          localStorage.removeItem(CART_SAVED_KEY);
          throw new Error('Could not prepare the selected items.');
        }
      } else {
        localStorage.removeItem(CART_SAVED_KEY);
      }
      sessionStorage.removeItem(CART_SELECTION_KEY);
      window.location.assign(`${window.Shopify?.routes?.root || '/'}checkout`);
    } catch (error) {
      setStatus(error.message || 'Something went wrong. Please try again.');
      document.querySelectorAll('[data-fv-checkout-selected]').forEach((checkoutButton) => {
        checkoutButton.disabled = false;
        checkoutButton.classList.remove('is-loading');
        checkoutButton.textContent = originalLabels.get(checkoutButton) || 'CHECKOUT SELECTED ITEMS →';
      });
      syncCartSelection();
    }
  };

  const restoreSavedCart = async (button = null, auto = false) => {
    const saved = safeRead(localStorage, CART_SAVED_KEY, { items: [] });
    if (!Array.isArray(saved.items) || !saved.items.length) return false;
    const originalLabel = button?.textContent || '';
    if (button) {
      button.disabled = true;
      button.textContent = 'Restoring…';
    }
    try {
      const response = await fetch(window.routes.cart_add_url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: saved.items }),
      });
      if (!response.ok) throw new Error('Could not restore saved items.');
      const added = await response.json();
      const nextSelection = {};
      (added.items || []).forEach((item) => {
        if (item.key) nextSelection[item.key] = false;
      });
      safeWrite(sessionStorage, CART_SELECTION_KEY, nextSelection);
      localStorage.removeItem(CART_SAVED_KEY);
      await refreshCartCount();
      if (auto) {
        sessionStorage.setItem('furavoraAutoRestoreDoneV1', '1');
      }
      window.location.assign(window.routes.cart_url);
      return true;
    } catch (_error) {
      if (button) {
        button.disabled = false;
        button.textContent = originalLabel;
      }
      return false;
    }
  };

  const autoRestoreSavedCart = () => {
    if (location.pathname !== window.routes?.cart_url) return;
    if (sessionStorage.getItem('furavoraAutoRestoreDoneV1') === '1') {
      sessionStorage.removeItem('furavoraAutoRestoreDoneV1');
      return;
    }
    const saved = safeRead(localStorage, CART_SAVED_KEY, { items: [] });
    if (!Array.isArray(saved.items) || !saved.items.length) return;
    document.documentElement.classList.add('fv-cart-auto-restoring');
    restoreSavedCart(null, true);
  };

  const initGlobalEvents = () => {
    if (document.documentElement.dataset.fv2EventsReady) return;
    document.documentElement.dataset.fv2EventsReady = 'true';

    document.addEventListener('click', (event) => {
      const checkoutButton = event.target.closest('[data-fv-checkout-selected]');
      if (checkoutButton) {
        event.preventDefault();
        checkoutSelected(checkoutButton);
        return;
      }
      const restoreButton = event.target.closest('[data-fv-restore-saved]');
      if (restoreButton) {
        event.preventDefault();
        restoreSavedCart(restoreButton, false);
      }
    });

    document.addEventListener('change', (event) => {
      if (event.target.matches('[data-fv-cart-select]')) {
        const key = event.target.dataset.lineKey;
        document.querySelectorAll('[data-fv-cart-select]').forEach((input) => {
          if (input.dataset.lineKey === key) input.checked = event.target.checked;
        });
        syncCartSelection();
      }
      if (event.target.matches('[data-fv-cart-select-all]')) {
        document.querySelectorAll('[data-fv-cart-select]').forEach((input) => { input.checked = event.target.checked; });
        syncCartSelection();
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      document.querySelectorAll('[data-fv2-menu][open], [data-fv2-search][open]').forEach(closeDetails);
    });

    document.addEventListener('cart:refresh', () => {
      refreshCartCount();
    });

    const cartObserver = new MutationObserver((mutations) => {
      const cartMarkupChanged = mutations.some((mutation) => [...mutation.addedNodes].some((node) => (
        node.nodeType === 1 && (
          node.matches?.('[data-fv-cart-select], [data-fv-saved-cart], [data-fv-cart-totals], [data-fv-cart-line]') ||
          node.querySelector?.('[data-fv-cart-select], [data-fv-saved-cart], [data-fv-cart-totals], [data-fv-cart-line]')
        )
      )));
      if (cartMarkupChanged) window.setTimeout(() => {
        syncCartSelection();
        syncSavedCartNotice();
      }, 20);
    });
    cartObserver.observe(document.body, { childList: true, subtree: true });
  };

  const initBackButtons = () => {
    document.querySelectorAll('[data-fv-back]').forEach((button) => {
      if (button.dataset.ready) return;
      button.dataset.ready = 'true';
      button.addEventListener('click', () => {
        if (history.length > 1 && document.referrer) history.back();
        else window.location.assign(button.dataset.fallback || '/');
      });
    });
  };

  const init = () => {
    initControls();
    initGlobalEvents();
    defaultCarrierToSmall();
    initHomeCategoryFilters();
    initCatalog();
    initBackButtons();
    syncCartSelection();
    syncSavedCartNotice();
    autoRestoreSavedCart();
  };

  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('cart:updated', () => window.setTimeout(init, 80));
})();
